import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BATCH_TRANSFER_MAX_IMPORT_BYTES,
  BATCH_TRANSFER_MAX_APPLY_CARDS,
  parseBatchIdentificationResults,
  type BatchIdentificationResults,
  type BatchImportApplyResponse,
  type BatchImportPreview,
  type BatchTransferHistory,
  type BatchTransferJob,
  type CardIdentity,
} from '@tcvs/shared';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { type BatchTransferApi, useBatchTransferApi } from './batch-transfer-api';

import type { ApiFailure } from '../auth/api-client';
import { CombinedPackageTools } from './CombinedPackageTools';
import { ChecklistCorrectionTools } from './ChecklistCorrectionTools';
import { ImportedResearchDetails } from './ImportedResearchDetails';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 60;

class BatchImportFileError extends Error {
  constructor(
    message: string,
    readonly reason:
      | 'INVALID_JSON'
      | 'INVALID_FORMAT'
      | 'FILE_TOO_LARGE'
      | 'WRONG_BATCH'
      | 'FILE_READ_FAILED',
  ) {
    super(message);
  }
}
export async function readBatchImportFile(
  file: File,
  batchId: string,
): Promise<BatchIdentificationResults> {
  if (file.size > BATCH_TRANSFER_MAX_IMPORT_BYTES)
    throw new BatchImportFileError('Choose a JSON file no larger than 2 MiB.', 'FILE_TOO_LARGE');
  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(new BatchImportFileError('This file could not be read.', 'FILE_READ_FAILED'));
    reader.readAsText(file);
  });
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new BatchImportFileError('This file is not valid JSON.', 'INVALID_JSON');
  }
  const parsed = parseBatchIdentificationResults(value);
  if (!parsed.ok) throw new BatchImportFileError(parsed.error, 'INVALID_FORMAT');
  if (parsed.value.batchId !== batchId)
    throw new BatchImportFileError('This result file belongs to a different batch.', 'WRONG_BATCH');
  return parsed.value;
}

function failureReportId(response: ApiFailure): string | null {
  if (!response.body || typeof response.body !== 'object') return null;
  const id = (response.body as Record<string, unknown>).reportId;
  return typeof id === 'string' ? id : null;
}
function diagnosticValue(value: unknown): string {
  if (value === undefined) return 'unmarked';
  return value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value);
}
function IdentityDetails({ identity }: { identity?: CardIdentity }) {
  if (!identity) return <span className="tcvs-muted">No identity</span>;
  return (
    <dl>
      {Object.entries(identity).map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
function JobSummary({ job }: { job: BatchTransferJob }) {
  if (job.kind === 'EXPORT') {
    const value = Number.isFinite(job.progress) ? job.progress! : job.status === 'READY' ? 100 : 0;
    const progress = Math.round(Math.min(100, Math.max(0, value)));
    return (
      <div className="tcvs-export-summary">
        <strong className="tcvs-export-job-status">Batch export · {job.status}</strong>
        <p className="tcvs-export-created">{job.createdAt || 'Preparing archive…'}</p>
        <div className="tcvs-export-counts">
          <span>
            <strong>{job.cardCount ?? '—'} </strong>
            <span>cards</span>
          </span>
          <span>
            <strong>{job.imageCount ?? '—'} </strong>
            <span>images</span>
          </span>
          <span>
            <strong>{job.missingImageCount ?? '—'} </strong>
            <span>missing images</span>
          </span>
        </div>
        <div className="tcvs-export-progress">
          <div
            className="tcvs-export-progress-track"
            role="progressbar"
            aria-label="Batch export progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <span className="tcvs-export-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="tcvs-export-progress-percent">{progress}%</span>
        </div>
        <p className="tcvs-export-image-note">
          Missing images, if any, are documented in the export.
        </p>
        {job.error ? <p className="tcvs-error">{job.error}</p> : null}
      </div>
    );
  }
  return (
    <>
      <strong>
        {job.kind === 'PACKAGE'
          ? 'Saved review package'
          : job.kind === 'REPORT'
            ? `Import report · ${job.stage ?? ''}`
            : `Import ${job.runId ?? ''}`}{' '}
        · {job.status}
      </strong>
      <p>
        {job.createdAt}
        {job.cardCount === undefined ? '' : ` · ${job.cardCount} cards`}
        {job.imageCount === undefined ? '' : ` · ${job.imageCount} images`}
      </p>
      {job.error ? <p className="tcvs-error">{job.error}</p> : null}
      {job.reason ? <p>{job.reason}</p> : null}
    </>
  );
}

/** Shared whole-batch controls. No API reads occur until an operator opens an action. */
export function BatchTransferTools({
  batchId,
  onImported,
  disabled = false,
}: {
  batchId: string;
  onImported: () => Promise<unknown> | void;
  disabled?: boolean;
}) {
  const { claims } = useAuth();
  const api = useBatchTransferApi();
  if (!claims || !hasAnyGroup(claims, ['reviewer', 'admin'])) return null;
  return (
    <BatchTransferPanel
      key={batchId}
      batchId={batchId}
      api={api}
      onImported={onImported}
      disabled={disabled}
      admin={hasAnyGroup(claims, ['admin'])}
    />
  );
}

export function BatchTransferPanel({
  batchId,
  api,
  onImported,
  disabled = false,
  admin = false,
}: {
  batchId: string;
  admin?: boolean;
  api: BatchTransferApi;
  onImported: () => Promise<unknown> | void;
  disabled?: boolean;
}) {
  const [mode, setMode] = useState<
    'EXPORT' | 'IMPORT' | 'HISTORY' | 'CORRECTIONS' | 'PACKAGE' | null
  >(null);
  const [busy, setBusy] = useState<'CREATE' | 'DOWNLOAD' | 'PREVIEW' | 'APPLY' | 'HISTORY' | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [packageId, setPackageId] = useState<string | undefined>();
  const [reportId, setReportId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [job, setJob] = useState<BatchTransferJob | null>(null);
  const [history, setHistory] = useState<BatchTransferHistory | null>(null);
  const [results, setResults] = useState<BatchIdentificationResults | null>(null);
  const [importMode, setImportMode] = useState<'DRAFT' | 'MANUAL_CONFIRMED'>('DRAFT');
  const [attested, setAttested] = useState(false);
  const [preview, setPreview] = useState<BatchImportPreview | null>(null);
  const [applied, setApplied] = useState<BatchImportApplyResponse | null>(null);
  const [selected, setSelected] = useState(new Set<string>());
  const [pollVersion, setPollVersion] = useState(0);
  const [downloadState, setDownloadState] = useState<'IDLE' | 'STARTED' | 'FAILED' | 'EXPIRED'>(
    'IDLE',
  );
  const automaticDownload = useRef<{ batchId: string; jobId: string } | null>(null);
  const downloadRequest = useRef<symbol | null>(null);
  const createRequest = useRef<symbol | null>(null);
  const menu = useRef<HTMLDetailsElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const alive = useRef(true);
  const generation = useRef(0);
  const controllers = useRef(new Set<AbortController>());
  const requestId = useRef<string | null>(null);
  const currentBatch = useRef(batchId);
  currentBatch.current = batchId;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      automaticDownload.current = null;
      downloadRequest.current = null;
      createRequest.current = null;
      generation.current += 1;
      for (const c of controllers.current) c.abort();
    };
  }, []);
  useEffect(() => {
    generation.current += 1;
    for (const c of controllers.current) c.abort();
    setMode(null);
    setPackageId(undefined);
    setJob(null);
    setPreview(null);
    setResults(null);
    setImportMode('DRAFT');
    setAttested(false);
    setHistory(null);
    setReportId(null);
    setApplied(null);
    setBusy(null);
    setError(null);
    setNotice(null);
    requestId.current = null;
    automaticDownload.current = null;
    downloadRequest.current = null;
    createRequest.current = null;
    setDownloadState('IDLE');
  }, [batchId]);
  const activeRequest = () => {
    const version = ++generation.current;
    const batch = batchId;
    const controller = new AbortController();
    controllers.current.add(controller);
    return {
      signal: controller.signal,
      current: () =>
        alive.current && version === generation.current && currentBatch.current === batch,
      done: () => controllers.current.delete(controller),
    };
  };
  const download = async (id: string) => {
    if (downloadRequest.current !== null) return;
    const token = Symbol(id);
    downloadRequest.current = token;
    setBusy('DOWNLOAD');
    setError(null);
    setNotice(null);
    const request = activeRequest();
    try {
      const result = await api.getJob(batchId, id, request.signal);
      if (!request.current() || downloadRequest.current !== token) return;
      if (!result.ok) {
        setDownloadState(result.status === 404 || result.status === 410 ? 'EXPIRED' : 'FAILED');
        setError(
          result.status === 404 || result.status === 410
            ? 'This archive is no longer available. Prepare a new export.'
            : 'A fresh download link could not be obtained. Retry download.',
        );
        return;
      }
      if (
        result.data.jobId !== id ||
        result.data.batchId !== batchId ||
        result.data.kind !== 'EXPORT' ||
        result.data.status !== 'READY'
      ) {
        setDownloadState('FAILED');
        setError(
          'This archive is not ready to download. Retry download or reopen it through history.',
        );
        return;
      }
      setJob(result.data);
      if (!result.data.downloadUrl) {
        setDownloadState('EXPIRED');
        setError('This archive’s download period has expired. Prepare a new export.');
        return;
      }
      if (new URL(result.data.downloadUrl).protocol !== 'https:')
        throw new Error('Invalid download link.');
      const anchor = document.createElement('a');
      anchor.href = result.data.downloadUrl;
      anchor.download = `tcvs-batch-${id}.zip`;
      anchor.rel = 'noreferrer';
      anchor.style.display = 'none';
      document.body.append(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
      }
      setDownloadState('STARTED');
      setNotice('Download started in your browser. Check its downloads panel for progress.');
    } catch {
      if (request.current() && downloadRequest.current === token) {
        setDownloadState('FAILED');
        setError('The download could not be started. Retry download to obtain a fresh link.');
      }
    } finally {
      request.done();
      if (downloadRequest.current === token) downloadRequest.current = null;
      if (request.current()) setBusy(null);
    }
  };
  useEffect(() => {
    const intent = automaticDownload.current;
    if (
      mode !== 'EXPORT' ||
      job?.status !== 'READY' ||
      intent?.batchId !== batchId ||
      intent.jobId !== job.jobId
    )
      return;
    automaticDownload.current = null;
    void download(job.jobId);
  }, [mode, job?.jobId, job?.status, batchId]);
  const close = () => {
    if (busy === 'APPLY') return;
    automaticDownload.current = null;
    downloadRequest.current = null;
    createRequest.current = null;
    generation.current += 1;
    for (const c of controllers.current) c.abort();
    setMode(null);
    setBusy(null);
  };
  useEffect(() => {
    if (!mode) return;
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const keys = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && busy !== 'APPLY') {
        event.preventDefault();
        close();
      }
      if (event.key !== 'Tab') return;
      const elements = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
        ) ?? [],
      );
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', keys);
    return () => {
      document.removeEventListener('keydown', keys);
      previous?.focus();
    };
  }, [mode, busy]);

  useEffect(() => {
    if (mode !== 'EXPORT' || !job || !['QUEUED', 'RUNNING'].includes(job.status)) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    let polls = 0;
    const controller = new AbortController();
    controllers.current.add(controller);
    const id = job.jobId;
    const batch = batchId;
    const poll = async () => {
      try {
        const result = await api.getJob(batch, id, controller.signal);
        if (!live || !alive.current || currentBatch.current !== batch) return;
        if (!result.ok) {
          setError(result.message ?? result.error);
          return;
        }
        setJob(result.data);
        if (['QUEUED', 'RUNNING'].includes(result.data.status)) {
          if (++polls < MAX_POLLS) timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
          else
            setNotice(
              'Preparation is continuing. Check progress again or return through Import history.',
            );
        }
      } catch {
        if (live)
          setError('Progress could not be checked. Check again or return through Import history.');
      }
    };
    timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
      controllers.current.delete(controller);
    };
  }, [mode, job?.jobId, pollVersion, batchId, api]);

  const open = (next: NonNullable<typeof mode>) => {
    if (menu.current) menu.current.open = false;
    setError(null);
    setNotice(null);
    if (next === 'CORRECTIONS' || next === 'PACKAGE') setReportId(null);
    setMode(next);
  };
  const create = async () => {
    if (createRequest.current !== null || downloadRequest.current !== null) return;
    const token = Symbol('create');
    createRequest.current = token;
    automaticDownload.current = null;
    setDownloadState('IDLE');
    open('EXPORT');
    setBusy('CREATE');
    setJob(null);
    const request = activeRequest();
    requestId.current ??= crypto.randomUUID();
    try {
      const result = await api.createExport(batchId, requestId.current, request.signal);
      if (!request.current()) return;
      if (!result.ok) setError(result.message ?? result.error);
      else {
        automaticDownload.current = ['QUEUED', 'RUNNING', 'READY'].includes(result.data.status)
          ? { batchId, jobId: result.data.jobId }
          : null;
        setJob(result.data);
        requestId.current = null;
      }
    } catch {
      if (request.current())
        setError(
          'Export preparation could not be confirmed. Retry uses the same request to avoid a duplicate job.',
        );
    } finally {
      if (createRequest.current === token) createRequest.current = null;
      request.done();
      if (request.current()) setBusy(null);
    }
  };
  const loadHistory = async () => {
    automaticDownload.current = null;
    open('HISTORY');
    setBusy('HISTORY');
    const request = activeRequest();
    try {
      const result = await api.history(batchId, request.signal);
      if (!request.current()) return;
      if (!result.ok) setError(result.message ?? result.error);
      else setHistory(result.data);
    } catch {
      if (request.current()) setError('History could not be loaded. Try again.');
    } finally {
      request.done();
      if (request.current()) setBusy(null);
    }
  };
  const resume = async (id: string) => {
    automaticDownload.current = null;
    setDownloadState('IDLE');
    open('EXPORT');
    setBusy('CREATE');
    setJob(null);
    const request = activeRequest();
    try {
      const result = await api.getJob(batchId, id, request.signal);
      if (!request.current()) return;
      if (!result.ok) setError(result.message ?? result.error);
      else {
        setJob(result.data);
        if (result.data.status === 'READY' && !result.data.downloadUrl) {
          setDownloadState('EXPIRED');
          setError('This archive’s download period has expired. Prepare a new export.');
        }
      }
    } catch {
      if (request.current())
        setError('This export could not be loaded. Try again through history.');
    } finally {
      request.done();
      if (request.current()) setBusy(null);
    }
  };
  const upload = async (file: File) => {
    setBusy('PREVIEW');
    setAttested(false);
    setError(null);
    setNotice(null);
    setPreview(null);
    setResults(null);
    setApplied(null);
    setReportId(null);
    setSelected(new Set());
    const request = activeRequest();
    try {
      const parsed = await readBatchImportFile(file, batchId);
      if (!request.current()) return;
      const result = await api.preview(batchId, parsed, request.signal, importMode);
      if (!request.current()) return;
      if (!result.ok) {
        setError(result.message ?? result.error);
        setReportId(failureReportId(result));
      } else {
        setResults(parsed);
        setPreview(result.data);
        setReportId(result.data.reportId ?? null);
        setSelected(
          new Set(
            result.data.rows.filter((row) => row.status === 'READY').map((row) => row.cardId),
          ),
        );
      }
    } catch (cause) {
      if (request.current()) {
        setError(cause instanceof Error ? cause.message : 'This file could not be previewed.');
        if (cause instanceof BatchImportFileError) {
          try {
            const logged = await api.logImportFailure(
              batchId,
              cause.reason,
              file.name.slice(0, 255),
              request.signal,
              importMode,
            );
            if (request.current()) {
              if (logged.ok) setReportId(logged.data.reportId);
              else
                setNotice(
                  'The file was rejected, but its report could not be saved. Try again when the connection is available.',
                );
            }
          } catch {
            if (request.current())
              setNotice(
                'The file was rejected, but its report could not be saved. Try again when the connection is available.',
              );
          }
        }
      }
    } finally {
      request.done();
      if (request.current()) setBusy(null);
    }
  };
  const changeImportMode = async (next: 'DRAFT' | 'MANUAL_CONFIRMED') => {
    if (busy !== null || next === importMode) return;
    setImportMode(next);
    setAttested(false);
    setPreview(null);
    setApplied(null);
    setSelected(new Set());
    setError(null);
    setNotice(null);
    if (!results) return;
    setBusy('PREVIEW');
    const request = activeRequest();
    try {
      const response = await api.preview(batchId, results, request.signal, next);
      if (!request.current()) return;
      if (!response.ok) {
        setError(response.message ?? response.error);
        setReportId(failureReportId(response));
      } else {
        setPreview(response.data);
        setReportId(response.data.reportId ?? null);
        setSelected(
          new Set(
            response.data.rows.filter((row) => row.status === 'READY').map((row) => row.cardId),
          ),
        );
      }
    } catch {
      if (request.current())
        setError('The selected import mode could not be previewed. Choose the result file again.');
    } finally {
      request.done();
      if (request.current()) setBusy(null);
    }
  };
  const apply = async () => {
    if (!results || !preview || (importMode === 'MANUAL_CONFIRMED' && !attested)) return;
    setBusy('APPLY');
    setError(null);
    setNotice(null);
    setApplied(null);
    const request = activeRequest();
    const selectedCardIds = [...selected];
    let aggregate: BatchImportApplyResponse | null = null;
    const confirmedRows = new Map<string, BatchImportApplyResponse['rows'][number]>();
    try {
      const chunks =
        selectedCardIds.length === 0
          ? [[] as string[]]
          : Array.from(
              { length: Math.ceil(selectedCardIds.length / BATCH_TRANSFER_MAX_APPLY_CARDS) },
              (_, index) =>
                selectedCardIds.slice(
                  index * BATCH_TRANSFER_MAX_APPLY_CARDS,
                  (index + 1) * BATCH_TRANSFER_MAX_APPLY_CARDS,
                ),
            );
      let checked = 0;
      for (const chunk of chunks) {
        if (!request.current()) return;
        const result = await api.apply(
          batchId,
          results,
          chunk,
          preview.previewToken,
          request.signal,
          importMode,
        );
        if (!request.current()) return;
        if (!result.ok) {
          setReportId(failureReportId(result));
          throw new Error(result.message ?? result.error);
        }
        setReportId(result.data.reportId ?? null);
        if (aggregate && aggregate.importId !== result.data.importId)
          throw new Error('Import history references did not agree.');
        const allowedIds = new Set(
          selectedCardIds.length === 0 ? results.cards.map((row) => row.cardId) : chunk,
        );
        const chunkRows = new Map(
          result.data.rows
            .filter((row) => allowedIds.has(row.cardId))
            .map((row) => [row.cardId, row]),
        );
        for (const [cardId, row] of chunkRows) confirmedRows.set(cardId, row);
        aggregate = { importId: result.data.importId, rows: [...confirmedRows.values()] };
        setApplied(aggregate);
        if (chunk.some((cardId) => !chunkRows.has(cardId)))
          throw new Error('Some selected outcomes could not be confirmed.');
        checked += chunk.length;
        setNotice(`Checked ${checked} of ${selectedCardIds.length} selected results.`);
      }
      setPreview(null);
      setSelected(new Set());
      setNotice(
        selectedCardIds.length === 0
          ? 'Research results recorded. No card identifications were changed.'
          : 'All selected results were checked. Review the per-card outcomes below.',
      );
    } catch {
      if (request.current()) {
        setPreview(null);
        setSelected(new Set());
        setError(
          `The import stopped; ${aggregate?.rows.length ?? 0} of ${selectedCardIds.length} result outcomes were confirmed. Check Import history before retrying. Upload the same result file again to preview the remaining cards; confirmed receipts prevent duplicate application.`,
        );
      }
    } finally {
      if (
        selectedCardIds.length > 0 &&
        request.current() &&
        aggregate?.rows.some((row) => row.status === 'APPLIED')
      ) {
        try {
          await onImported();
        } catch {
          if (request.current())
            setNotice(
              'Some results were applied. Reload this batch to see the saved identifications.',
            );
        }
      }
      request.done();
      if (request.current()) setBusy(null);
    }
  };

  const downloadReport = async (id: string) => {
    setBusy('DOWNLOAD');
    setError(null);
    const request = activeRequest();
    try {
      const response = await api.getJob(batchId, id, request.signal);
      if (!request.current()) return;
      if (!response.ok) throw new Error(response.message ?? response.error);
      if (
        response.data.batchId !== batchId ||
        response.data.jobId !== id ||
        response.data.kind !== 'REPORT' ||
        !response.data.downloadUrl ||
        new URL(response.data.downloadUrl).protocol !== 'https:'
      )
        throw new Error('This report is unavailable.');
      const anchor = document.createElement('a');
      anchor.href = response.data.downloadUrl;
      anchor.download = `tcvs-import-report-${id}.json`;
      anchor.rel = 'noreferrer';
      document.body.append(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
      }
      setNotice('Report download started in your browser.');
    } catch {
      if (request.current())
        setError('The report could not be downloaded. Reopen it through history to try again.');
    } finally {
      request.done();
      if (request.current()) setBusy(null);
    }
  };
  return (
    <>
      <details className="tcvs-batch-transfer-menu" ref={menu}>
        <summary>Batch tools</summary>
        <div className="tcvs-batch-transfer-options">
          <button type="button" disabled={disabled || busy !== null} onClick={() => void create()}>
            Download batch ZIP
          </button>
          <button type="button" disabled={disabled || busy !== null} onClick={() => open('IMPORT')}>
            Import identification results
          </button>
          <button
            type="button"
            disabled={disabled || busy !== null}
            onClick={() => {
              setPackageId(undefined);
              open('PACKAGE');
            }}
          >
            Import review package
          </button>
          <button
            type="button"
            disabled={disabled || busy !== null}
            onClick={() => open('CORRECTIONS')}
          >
            Checklist corrections
          </button>
          <button type="button" disabled={busy !== null} onClick={() => void loadHistory()}>
            Import history
          </button>
        </div>
      </details>
      {mode === null
        ? null
        : createPortal(
            <div className="tcvs-batch-transfer-backdrop">
              <section
                ref={dialog}
                className="tcvs-batch-transfer-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tcvs-transfer-title"
              >
                <header>
                  <h3 id="tcvs-transfer-title">
                    {mode === 'EXPORT'
                      ? 'Download batch ZIP'
                      : mode === 'IMPORT'
                        ? 'Import identification results'
                        : mode === 'PACKAGE'
                          ? 'Import review package'
                          : mode === 'CORRECTIONS'
                            ? 'Checklist corrections'
                            : 'Batch transfer history'}
                  </h3>
                  <button type="button" disabled={busy === 'APPLY'} onClick={close}>
                    Close
                  </button>
                </header>
                <p className="tcvs-muted">Entire batch · {batchId}</p>
                <div className={mode === 'EXPORT' ? 'tcvs-export-feedback' : undefined}>
                  {error ? (
                    <p className="tcvs-error" role="alert">
                      {error}
                    </p>
                  ) : null}
                  {notice ? <p role="status">{notice}</p> : null}
                  {busy && mode !== 'CORRECTIONS' && mode !== 'PACKAGE' ? (
                    <p role="status">
                      {busy === 'CREATE'
                        ? 'Preparing export…'
                        : busy === 'DOWNLOAD'
                          ? 'Starting browser download…'
                          : busy === 'PREVIEW'
                            ? 'Checking results…'
                            : busy === 'APPLY'
                              ? importMode === 'MANUAL_CONFIRMED'
                                ? 'Confirming selected identifications…'
                                : 'Applying selected review drafts…'
                              : 'Loading history…'}
                    </p>
                  ) : null}
                </div>
                {(mode === 'IMPORT' || mode === 'CORRECTIONS' || mode === 'PACKAGE') && reportId ? (
                  <p>
                    Saved report: {reportId}{' '}
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void downloadReport(reportId)}
                    >
                      Download import report
                    </button>
                  </p>
                ) : null}
                {mode === 'EXPORT' ? (
                  <>
                    <p>
                      Includes all current batch cards, available original images, OCR and saved
                      identification data. The ZIP includes the engineer’s return template, JSON
                      schema and local image viewer.
                    </p>
                    <div className="tcvs-export-actions">
                      <button
                        type="button"
                        disabled={
                          busy !== null || job?.status !== 'READY' || downloadState === 'EXPIRED'
                        }
                        onClick={() => {
                          if (job) void download(job.jobId);
                        }}
                      >
                        {busy === 'DOWNLOAD'
                          ? 'Starting download…'
                          : downloadState === 'STARTED'
                            ? 'Download again'
                            : downloadState === 'FAILED'
                              ? 'Retry download'
                              : 'Download ZIP'}
                      </button>
                      <button
                        type="button"
                        disabled={
                          busy !== null ||
                          !(
                            (job && ['QUEUED', 'RUNNING', 'FAILED'].includes(job.status)) ||
                            downloadState === 'EXPIRED' ||
                            !job
                          )
                        }
                        style={{
                          visibility:
                            job?.status === 'READY' && downloadState !== 'EXPIRED'
                              ? 'hidden'
                              : 'visible',
                        }}
                        onClick={() => {
                          if (job && ['QUEUED', 'RUNNING'].includes(job.status)) {
                            setError(null);
                            setNotice(null);
                            setPollVersion((v) => v + 1);
                          } else void create();
                        }}
                      >
                        {job && ['QUEUED', 'RUNNING'].includes(job.status)
                          ? 'Check progress again'
                          : job?.status === 'FAILED' || downloadState === 'EXPIRED'
                            ? 'Prepare new export'
                            : 'Retry export'}
                      </button>
                    </div>
                    <JobSummary
                      job={
                        job ?? {
                          jobId: '',
                          batchId,
                          kind: 'EXPORT',
                          status: 'QUEUED',
                          createdAt: '',
                        }
                      }
                    />
                    <p className="tcvs-muted">
                      ZIP downloads expire after one day. Return-file references remain valid for 30
                      days. You can close this window and resume through history.
                    </p>
                  </>
                ) : null}
                {mode === 'IMPORT' ? (
                  <>
                    <p>
                      Choose the versioned JSON returned from an exported batch (maximum 2 MiB).
                      Preview saves a diagnostic report without changing cards. Choose whether to
                      save review drafts or confirm identifications that were manually checked.
                    </p>
                    <fieldset disabled={busy !== null || disabled} className="tcvs-transfer-mode">
                      <legend>Identification import</legend>
                      <label>
                        <input
                          type="radio"
                          name="identification-import-mode"
                          checked={importMode === 'DRAFT'}
                          onChange={() => void changeImportMode('DRAFT')}
                        />{' '}
                        Review drafts
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="identification-import-mode"
                          checked={importMode === 'MANUAL_CONFIRMED'}
                          onChange={() => void changeImportMode('MANUAL_CONFIRMED')}
                        />{' '}
                        Manually confirmed identifications
                      </label>
                    </fieldset>
                    {importMode === 'MANUAL_CONFIRMED' ? (
                      <label className="tcvs-transfer-attestation">
                        <input
                          type="checkbox"
                          checked={attested}
                          disabled={busy !== null || disabled}
                          onChange={(event) => setAttested(event.currentTarget.checked)}
                        />{' '}
                        I confirm the selected cards were manually checked against their images.
                        Supplied identification fields replace current values; listing, pricing and
                        image approval remain separate.
                      </label>
                    ) : null}
                    <label>
                      Result JSON{' '}
                      <input
                        type="file"
                        accept=".json,application/json"
                        disabled={busy !== null || disabled}
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          event.currentTarget.value = '';
                          if (file) void upload(file);
                        }}
                      />
                    </label>
                    {preview ? (
                      <>
                        <p>
                          Run {preview.runId} · {selected.size} of {preview.rows.length} results
                          selected
                        </p>
                        <div className="tcvs-transfer-actions">
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() =>
                              setSelected(
                                new Set(
                                  preview.rows
                                    .filter((row) => row.status === 'READY')
                                    .map((row) => row.cardId),
                                ),
                              )
                            }
                          >
                            Select eligible results
                          </button>
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => setSelected(new Set())}
                          >
                            Clear selection
                          </button>
                        </div>
                        <div className="tcvs-transfer-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Apply</th>
                                <th>Card</th>
                                <th>Current</th>
                                <th>
                                  {importMode === 'MANUAL_CONFIRMED'
                                    ? 'Confirmed identity to save'
                                    : 'Proposed draft'}
                                </th>
                                <th>Result / evidence</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.rows.map((row) => (
                                <tr key={row.cardId}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      aria-label={`Apply ${row.shortCode ?? row.cardId}`}
                                      disabled={row.status !== 'READY' || busy !== null}
                                      checked={selected.has(row.cardId)}
                                      onChange={(event) => {
                                        const checked = event.currentTarget.checked;
                                        setSelected((old) => {
                                          const next = new Set(old);
                                          if (checked) next.add(row.cardId);
                                          else next.delete(row.cardId);
                                          return next;
                                        });
                                      }}
                                    />
                                  </td>
                                  <td>
                                    {row.shortCode ?? row.cardId}
                                    <small>
                                      <br />
                                      {row.cardId}
                                    </small>
                                  </td>
                                  <td>
                                    <IdentityDetails identity={row.current} />
                                  </td>
                                  <td>
                                    <IdentityDetails identity={row.proposed} />
                                    {row.checklistSourceSelection ? (
                                      <p>Exact checklist selection supplied.</p>
                                    ) : row.status === 'READY' ? (
                                      <p>
                                        {importMode === 'MANUAL_CONFIRMED'
                                          ? 'Manually confirmed. Checklist changes are handled separately.'
                                          : 'Text-only draft; checklist confirmation still required.'}
                                      </p>
                                    ) : null}
                                  </td>
                                  <td>
                                    {row.clearsHierarchy ? (
                                      <p>
                                        Previous set/subset/parallel assignment will be replaced.
                                      </p>
                                    ) : null}
                                    {row.clearsUnverifiedSource ? (
                                      <p>Previous unverified checklist source will be removed.</p>
                                    ) : null}
                                    <strong>{row.status.replaceAll('_', ' ')}</strong>
                                    {row.reason ? <p>{row.reason}</p> : null}
                                    {row.resolution ? <p>{row.resolution}</p> : null}
                                    {row.catalogWarnings?.length ? (
                                      <section aria-label="Checklist differences">
                                        <strong>
                                          {importMode === 'MANUAL_CONFIRMED' &&
                                          row.status === 'READY'
                                            ? 'Manual identification can be accepted with checklist differences'
                                            : 'Checklist reference differences'}
                                        </strong>
                                        <ul>
                                          {row.catalogWarnings.map((warning, index) => (
                                            <li key={index}>
                                              {warning.message}
                                              {warning.field
                                                ? ` · ${warning.field}: ${diagnosticValue(warning.current)} → ${diagnosticValue(warning.proposed)}`
                                                : ''}
                                              <p>{warning.resolution}</p>
                                            </li>
                                          ))}
                                        </ul>
                                      </section>
                                    ) : null}
                                    {row.confidence === undefined ? null : (
                                      <p>
                                        Reported confidence: {Math.round(row.confidence * 100)}%
                                        (advisory)
                                      </p>
                                    )}
                                    <ImportedResearchDetails research={row} />
                                    <ul>
                                      {row.evidence?.map((note, index) => (
                                        <li key={index}>{note}</li>
                                      ))}
                                    </ul>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {selected.size === 0 ? (
                          <p>
                            No card identifications will change. Record this run and its
                            uncertain/unselected results in history.
                          </p>
                        ) : null}
                        <button
                          type="button"
                          disabled={
                            busy !== null ||
                            disabled ||
                            (importMode === 'MANUAL_CONFIRMED' && !attested)
                          }
                          onClick={() => void apply()}
                        >
                          {selected.size === 0
                            ? 'Record results'
                            : importMode === 'MANUAL_CONFIRMED'
                              ? `Confirm identifications (${selected.size})`
                              : `Apply selected results (${selected.size})`}
                        </button>
                      </>
                    ) : null}
                    {applied ? (
                      <>
                        <p role="status">
                          {busy === 'APPLY'
                            ? 'Import progress. '
                            : error
                              ? 'Confirmed results. '
                              : 'Import finished. '}
                          {applied.rows.filter((row) => row.status === 'APPLIED').length}{' '}
                          {importMode === 'MANUAL_CONFIRMED'
                            ? 'identifications confirmed.'
                            : 'review drafts saved.'}
                        </p>
                        <ul>
                          {applied.rows.map((row) => (
                            <li key={row.cardId}>
                              {row.cardId} · {row.status.replaceAll('_', ' ')}
                              {row.reason ? ` · ${row.reason}` : ''}
                              {row.resolution ? <p>{row.resolution}</p> : null}
                              {row.catalogWarnings?.map((warning, index) => (
                                <p key={index}>
                                  {warning.message} {warning.resolution}
                                </p>
                              ))}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </>
                ) : null}
                {mode === 'PACKAGE' ? (
                  <CombinedPackageTools
                    key={`${batchId}:${packageId ?? 'new'}`}
                    batchId={batchId}
                    api={api}
                    admin={admin}
                    disabled={disabled}
                    resumeId={packageId}
                    onReport={setReportId}
                    onApplying={(applying) => setBusy(applying ? 'APPLY' : null)}
                    onImported={onImported}
                  />
                ) : null}
                {mode === 'CORRECTIONS' ? (
                  <ChecklistCorrectionTools
                    batchId={batchId}
                    api={api}
                    admin={admin}
                    disabled={disabled}
                    onReport={setReportId}
                    onApplying={(applying) => setBusy(applying ? 'APPLY' : null)}
                  />
                ) : null}
                {mode === 'HISTORY' ? (
                  <>
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void loadHistory()}
                    >
                      Refresh history
                    </button>
                    {history?.jobs.length === 0 ? <p>No batch transfers yet.</p> : null}
                    {history?.jobs.map((entry) => (
                      <div className="tcvs-transfer-job" key={entry.jobId}>
                        <JobSummary job={entry} />
                        {entry.kind === 'PACKAGE' ? (
                          <>
                            <p>
                              Saved review package ·{' '}
                              {entry.importMode === 'MANUAL_CONFIRMED'
                                ? 'Manual confirmation'
                                : 'Drafts'}
                            </p>
                            <button
                              type="button"
                              disabled={busy !== null}
                              onClick={() => {
                                setPackageId(entry.packageId ?? entry.jobId);
                                open('PACKAGE');
                              }}
                            >
                              Resume review
                            </button>
                          </>
                        ) : entry.kind === 'EXPORT' ? (
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => void resume(entry.jobId)}
                          >
                            Open export
                          </button>
                        ) : entry.kind === 'REPORT' ? (
                          <>
                            <p>Report {entry.reportId ?? entry.jobId}</p>
                            <button
                              type="button"
                              disabled={busy !== null}
                              onClick={() => void downloadReport(entry.jobId)}
                            >
                              Download report
                            </button>
                          </>
                        ) : (
                          <details>
                            <summary>Import outcomes</summary>
                            <p>
                              {entry.producer?.tool} · {entry.appliedCount ?? 0} saved drafts
                            </p>
                            <ul>
                              {entry.outcomes?.map((row) => (
                                <li key={row.cardId}>
                                  {row.cardId} · {row.status.replaceAll('_', ' ')}
                                  {row.reason ? ` · ${row.reason}` : ''}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    ))}
                    {history?.cursor ? (
                      <p className="tcvs-muted">Showing the most recent transfers.</p>
                    ) : null}
                  </>
                ) : null}
              </section>
            </div>,
            document.body,
          )}
    </>
  );
}
